import {
  booleanAttribute,
  Component,
  ViewEncapsulation,
  computed,
  inject,
  input,
} from '@angular/core';
import { MatIcon } from '@angular/material/icon';
import { TooltipDirective } from '../../auxiliares/tooltip/tooltip.directive';
import { BotonIconoComponent } from '../botones';
import { RouterLink } from '@angular/router';
import { HelperService } from '../../auxiliares/servicios/helper.service';

/**
 * Piezas del diseño de detalle de entidad (skill `detalle-entidad`). EN
 * PRUEBA: se extrajeron del detalle de tracker para usarlas en los demás
 * detalles; si no convencen, se vuelve al markup propio de cada detalle.
 *
 * Todas comparten `detalle.scss` (clases `dt-`, solo tokens del tema).
 */

/** Tarjeta con título en mayúsculas e ícono. El contenido va proyectado. */
@Component({
  selector: 'app-dt-card',
  standalone: true,
  imports: [MatIcon],
  template: `
    @if (titulo()) {
      <h3 class="dt-titulo">
        @if (icono()) {
          <mat-icon>{{ icono() }}</mat-icon>
        }
        {{ titulo() }}
      </h3>
    }
    <ng-content />
  `,
  styleUrl: './detalle.scss',
  encapsulation: ViewEncapsulation.None,
  host: { class: 'dt-card' },
})
export class DtCardComponent {
  public titulo = input<string>();
  public icono = input<string>();
}

/**
 * Grilla de datos (etiqueta arriba, valor abajo). `destacado` es la del
 * resumen: valores más grandes y columnas más angostas.
 */
@Component({
  selector: 'app-dt-datos',
  standalone: true,
  template: `
    <ng-content />
  `,
  styleUrl: './detalle.scss',
  encapsulation: ViewEncapsulation.None,
  host: { class: 'dt-datos', '[class.destacado]': 'destacado()' },
})
export class DtDatosComponent {
  public destacado = input(false, { transform: booleanAttribute });
}

/**
 * Un dato. Sin valor muestra la cápsula "Sin información" (nunca un guion) y
 * la etiqueta queda centrada sobre ella. Lo proyectado (botones, badges, una
 * fecha secundaria) va después del valor.
 */
@Component({
  selector: 'app-dt-dato',
  standalone: true,
  imports: [MatIcon, BotonIconoComponent, TooltipDirective],
  template: `
    <span class="dt-label">{{ etiqueta() }}</span>
    <span class="dt-valor" [class.dt-mono]="mono() && tieneValor()">
      @if (tieneValor()) {
        <span class="dt-texto">{{ valor() }}</span>
        @if (copiable()) {
          <button
            irxBotonIcono
            tamano="mini"
            class="dt-icono"
            (click)="copiar()"
            tooltip="Copiar"
          >
            <mat-icon>content_copy</mat-icon>
          </button>
        }
      } @else if (!ocultarVacio()) {
        <span class="dt-vacio">Sin información</span>
      }
      <ng-content />
    </span>
  `,
  styleUrl: './detalle.scss',
  encapsulation: ViewEncapsulation.None,
  host: {
    class: 'dt-dato',
    '[class.vacio]': '!tieneValor() && !ocultarVacio()',
  },
})
export class DtDatoComponent {
  private helper = inject(HelperService);

  public etiqueta = input.required<string>();
  public valor = input<string | number | null>();
  /** Datos técnicos (IDs, ICCID, EUIs): monoespaciada. */
  public mono = input(false, { transform: booleanAttribute });
  public copiable = input(false, { transform: booleanAttribute });
  /** Cuando el valor lo arma el contenido proyectado (sin cápsula). */
  public ocultarVacio = input(false, { transform: booleanAttribute });

  public tieneValor = computed(() => {
    const v = this.valor();
    return v !== undefined && v !== null && v !== '';
  });

  public copiar(): void {
    navigator.clipboard.writeText(String(this.valor())).then(
      () => this.helper.notifSuccess('Copiado al portapapeles'),
      () => this.helper.notifWarn('No se pudo copiar'),
    );
  }
}

/**
 * Cápsula de estado con punto, en los colores semánticos de la marca: verde
 * ok, naranja advertencia, rojo alerta, gris sin conexión.
 */
export type TonoEstado = 'ok' | 'advertencia' | 'alerta' | 'apagado' | 'neutro';

@Component({
  selector: 'app-dt-estado',
  standalone: true,
  template: `
    <span class="dt-dot"></span>
    {{ etiqueta() }}
  `,
  styleUrl: './detalle.scss',
  encapsulation: ViewEncapsulation.None,
  host: { '[class]': "'dt-estado ' + tono()" },
})
export class DtEstadoComponent {
  public etiqueta = input.required<string>();
  public tono = input<TonoEstado>('neutro');
}

/** Una SIM del equipo, ya armada para mostrar. */
export interface SimDetalle {
  /** "SIM 1", "SIM 2". */
  slot: string;
  /** Al detalle de la SIM, solo con permiso para verlas. */
  ruta?: string[];
  iccid?: string;
  numero?: string;
  operador?: string;
  apn?: string;
  usuario?: string;
  password?: string;
}

/** Las SIMs del equipo, una al lado de la otra; la vacía dice "Vacía". */
@Component({
  selector: 'app-dt-sims',
  standalone: true,
  imports: [MatIcon, RouterLink],
  template: `
    @for (s of sims(); track s.slot) {
      <div class="dt-sim" [class.vacia]="!s.iccid && !s.numero">
        <div class="dt-sim-cabecera">
          <mat-icon>sim_card</mat-icon>
          <strong>{{ s.slot }}</strong>
          @if (s.ruta) {
            <a class="irx-enlace dt-link" [routerLink]="s.ruta">Ver SIM</a>
          }
        </div>
        @if (s.iccid || s.numero) {
          <span class="dt-mono">{{ s.numero ?? 'Sin número' }}</span>
          <span class="dt-muted dt-mono">{{ s.iccid }}</span>
          <span class="dt-muted">
            {{ s.operador }}{{ s.apn ? ' · ' + s.apn : '' }}
          </span>
          @if (s.usuario || s.password) {
            <span class="dt-muted dt-mono">
              {{ s.usuario ?? 'Sin usuario' }} /
              {{ s.password ?? 'Sin contraseña' }}
            </span>
          }
        } @else {
          <span class="dt-muted">Vacía</span>
        }
      </div>
    }
  `,
  styleUrl: './detalle.scss',
  encapsulation: ViewEncapsulation.None,
  host: { class: 'dt-sims' },
})
export class DtSimsComponent {
  public sims = input.required<SimDetalle[]>();
}

/** Para importar todas juntas. */
export const PIEZAS_DETALLE = [
  DtCardComponent,
  DtDatosComponent,
  DtDatoComponent,
  DtEstadoComponent,
  DtSimsComponent,
] as const;
